import { useEffect, useCallback, useMemo, useReducer } from 'react'
import { Driver } from '@/core/domain/entities/Driver'
import {
  IDriverService,
  IRideService,
} from '@/core/application/services/interfaces'
import { container, TYPES } from '@/core/container'
import { isOk } from '@/core/shared/utils/Result'
import { UserId } from '@/core/shared/types'
import { useAuth } from '@/presentation/hooks/useAuth'
import { Period } from '@/core/shared/types/dashboard'
import {
  getPeriodDateRange,
  getPreviousPeriodDateRange,
} from '@/core/shared/utils/period'

interface DateRange {
  start: string
  end: string
}

interface DriverPerformanceAggregates {
  completedRides: number
  totalRides: number
  comissionEarnings: number
  averageRating: number
  cancellationRate: number
  responseTime: number
  onlineHours: number
  ridesPerDay: number
}

interface PreviousPeriodData {
  ridesCompleted: number
  commission: number
  rating: string
}

interface FormattedDriverData {
  id: string
  name: string
  avatar: string
  status: 'online' | 'offline'
  rating: string
  classification: string
  lastRideAt: string
  phone: string
  email: string
  address: string
  joinDate: string
}

interface FormattedVehicleInfo {
  model: string
  plate: string
  color: string
}

interface FormattedDocuments {
  cnh: { status: string; expires: string }
  ipva: { expires: string }
  insurance: { expires: string }
}

interface OnlineTimeData {
  tempoTotal: number
  diasOnline: number
  tempoMedioPorDia: number
  dataInicio: string
  dataFim: string
}

interface UseDriverDetailParams {
  driverId: string
  initialPeriod?: Period
}

interface UseDriverDetailReturn {
  driverData: FormattedDriverData | null
  vehicleInfo: FormattedVehicleInfo | null
  documents: FormattedDocuments | null
  onlineTime: OnlineTimeData | null

  currentPeriodData: {
    ridesCompleted: number
    commission: number
    rating: string
  } | null
  previousPeriodData: {
    ridesCompleted: number
    commission: number
    rating: string
  }
  performanceData: {
    hoursWorked: number
    acceptanceRate: number
    commissionPerRide: number
    ridesPerHour: number
  }

  selectedPeriod: Period
  dateRange: DateRange
  isLoading: boolean
  hasError: boolean
  error: string | null

  handlePeriodChange: (period: Period, customDates?: { startDate: string; endDate: string }) => void
  setDateRange: (range: DateRange) => void
  refreshData: () => void

  hasData: boolean
  hasAggregates: boolean
}

interface DriverDetailState {
  driver: Driver | null
  aggregates: DriverPerformanceAggregates | null
  previousPeriodData: PreviousPeriodData | null
  onlineTime: OnlineTimeData | null
  selectedPeriod: Period
  dateRange: DateRange
  isLoading: boolean
  error: string | null
}

type DriverDetailAction =
  | { type: 'SET_DRIVER'; payload: Driver }
  | { type: 'SET_AGGREGATES'; payload: DriverPerformanceAggregates }
  | { type: 'SET_PREVIOUS_PERIOD_DATA'; payload: PreviousPeriodData }
  | {
      type: 'SET_ONLINE_TIME'
      payload: OnlineTimeData
    }
  | { type: 'SET_PERIOD'; payload: Period }
  | { type: 'SET_DATE_RANGE'; payload: DateRange }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'RESET_DATA' }

const driverDetailReducer = (
  state: DriverDetailState,
  action: DriverDetailAction
): DriverDetailState => {
  switch (action.type) {
    case 'SET_DRIVER':
      return { ...state, driver: action.payload }
    case 'SET_AGGREGATES':
      return { ...state, aggregates: action.payload }
    case 'SET_PREVIOUS_PERIOD_DATA':
      return { ...state, previousPeriodData: action.payload }
    case 'SET_ONLINE_TIME':
      return { ...state, onlineTime: action.payload }
    case 'SET_PERIOD':
      return { ...state, selectedPeriod: action.payload }
    case 'SET_DATE_RANGE':
      return { ...state, dateRange: action.payload }
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'RESET_DATA':
      return {
        ...state,
        driver: null,
        aggregates: null,
        previousPeriodData: null,
        onlineTime: null,
        error: null,
      }
    default:
      return state
  }
}

export const useDriverDetail = ({
  driverId,
  initialPeriod = Period.Today,
}: UseDriverDetailParams): UseDriverDetailReturn => {
  const { user } = useAuth()

  const initialStateWithPeriod: DriverDetailState = {
    driver: null,
    aggregates: null,
    previousPeriodData: null,
    onlineTime: null,
    selectedPeriod: initialPeriod,
    dateRange: getPeriodDateRange(initialPeriod),
    isLoading: false,
    error: null,
  }
  const [state, dispatch] = useReducer(
    driverDetailReducer,
    initialStateWithPeriod
  )

  const driverService = container.resolve<IDriverService>(TYPES.DriverService)
  const rideService = container.resolve<IRideService>(TYPES.RideService)

  const fetchDriverDetails = useCallback(
    async (userId: UserId, driverId: string) => {
      console.log('🔍 [useDriverDetail] Fetching driver details:', {
        userId,
        driverId,
        hasUserId: !!userId,
        hasDriverId: !!driverId,
      })

      if (!userId || !driverId) {
        console.warn(
          '⚠️ [useDriverDetail] Missing userId or driverId for driver details fetch',
          {
            userId,
            driverId,
          }
        )
        return
      }

      try {
        console.log(
          '📡 [useDriverDetail] Calling driverService.getDriverDetails...'
        )
        const result = await driverService.getDriverDetails({
          userId,
          driverId,
        })

        console.log('📦 [useDriverDetail] Result received:', {
          isOk: isOk(result),
          result: result,
        })

        if (!isOk(result)) {
          console.error('❌ [useDriverDetail] Error in result:', result.error)
          throw new Error(result.error.message)
        }

        console.log(
          '✅ [useDriverDetail] Driver loaded successfully:',
          result.value
        )
        dispatch({ type: 'SET_DRIVER', payload: result.value })
      } catch (err) {
        console.error(
          '❌ [useDriverDetail] Failed to fetch driver details:',
          err
        )
        throw err
      }
    },
    [driverService]
  )

  const fetchDriverRideMetrics = useCallback(
    async (driverId: string, dateRange: DateRange) => {
      if (!driverId || !user?.id) {
        return { totalRides: 0, comissionEarnings: 0 }
      }

      try {
        const result = await rideService.getDashboardData({
          userId: user.id,
          dateStart: dateRange.start,
          dateEnd: dateRange.end,
        })

        if (!isOk(result)) {
          return { totalRides: 0, comissionEarnings: 0 }
        }

        const rideMetrics = result.value.rideMetrics
        const driverStats = rideMetrics.ridesPerDriverList?.find(
          (stat) => stat.driverId === driverId
        )

        return {
          totalRides: driverStats?.totalRides ?? 0,
          comissionEarnings: driverStats?.comissionEarnings ?? 0,
        }
      } catch (err) {
        console.error('❌ [useDriverDetail] Failed to fetch ride metrics:', err)
        return { totalRides: 0, comissionEarnings: 0 }
      }
    },
    [rideService, user?.id]
  )

  const fetchDriverPerformance = useCallback(
    async (driverId: string, dateRange: DateRange) => {
      console.log('📊 [useDriverDetail] Fetching driver performance:', {
        driverId,
        dateRange,
        selectedPeriod: state.selectedPeriod,
      })

      if (!driverId) {
        console.warn(
          '⚠️ [useDriverDetail] Missing driverId for performance fetch'
        )
        return
      }

      try {
        console.log(
          '📡 [useDriverDetail] Calling driverService.getDriverPerformance...'
        )
        const result = await driverService.getDriverPerformance({
          userId: user?.id ?? '',
          driverId,
          period: state.selectedPeriod,
        })

        console.log('📦 [useDriverDetail] Performance result:', {
          isOk: isOk(result),
          hasValue: isOk(result) && !!result.value,
          result: result,
        })

        if (!isOk(result)) {
          console.error(
            '❌ [useDriverDetail] Error in performance:',
            result.error
          )
          throw new Error(result.error.message)
        }

        if (!result.value) {
          console.warn(
            '⚠️ [useDriverDetail] Performance result is empty, skipping aggregates'
          )
          return
        }

        const performance = result.value

        console.log('🔍 [useDriverDetail] Performance data:', {
          finishedRides: performance.finishedRides,
          rating: performance.rating,
          hasFinishedRides: typeof performance.finishedRides !== 'undefined',
          hasRating: typeof performance.rating !== 'undefined',
        })

        const rideMetrics = await fetchDriverRideMetrics(driverId, dateRange)

        const startDate = new Date(dateRange.start)
        const endDate = new Date(dateRange.end)
        const daysDiff = Math.max(
          1,
          Math.ceil(
            (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
          )
        )
        const enhancedAggregates: DriverPerformanceAggregates = {
          completedRides: performance.finishedRides ?? 0,
          totalRides: rideMetrics.totalRides,
          comissionEarnings: rideMetrics.comissionEarnings,
          averageRating: performance.rating ?? 0,
          cancellationRate: 0,
          responseTime: 0,
          onlineHours: 0,
          ridesPerDay: rideMetrics.totalRides / daysDiff,
        }

        console.log(
          '✅ [useDriverDetail] Aggregates calculated:',
          enhancedAggregates
        )
        dispatch({ type: 'SET_AGGREGATES', payload: enhancedAggregates })
      } catch (err) {
        console.error(
          '❌ [useDriverDetail] Failed to fetch driver performance:',
          err
        )
        throw err
      }
    },
    [driverService, state.selectedPeriod, user?.id, fetchDriverRideMetrics]
  )

  const fetchDriverOnlineTime = useCallback(
    async (driverId: string, dateRange: DateRange) => {
      console.log('⏱️ [useDriverDetail] Fetching driver online time:', {
        driverId,
        dateRange,
      })

      if (!driverId || !user?.id) {
        console.warn(
          '⚠️ [useDriverDetail] Missing driverId or userId for online time fetch'
        )
        return null
      }

      try {
        console.log(
          '📡 [useDriverDetail] Calling driverService.getDriverOnlineTime...'
        )
        const result = await driverService.getDriverOnlineTime({
          userId: user.id,
          driverId,
          dateStart: dateRange.start,
          dateEnd: dateRange.end,
        })

        console.log('📦 [useDriverDetail] Online time result:', {
          isOk: isOk(result),
          hasValue: isOk(result) && !!result.value,
        })

        if (!isOk(result)) {
          console.error(
            '❌ [useDriverDetail] Error in online time:',
            result.error
          )
          return null
        }

        const onlineTimeData: OnlineTimeData = {
          tempoTotal: (result.value as { onlineTime?: number }).onlineTime || 0,
          diasOnline: (result.value as { diasOnline?: number }).diasOnline || 0,
          tempoMedioPorDia:
            (result.value as { tempoMedioPorDia?: number }).tempoMedioPorDia ||
            0,
          dataInicio:
            (result.value as { dataInicio?: string }).dataInicio || '',
          dataFim: (result.value as { dataFim?: string }).dataFim || '',
        }

        console.log('🔍 [useDriverDetail] Online time data:', {
          driverId,
          onlineTimeData,
        })

        return onlineTimeData
      } catch (err) {
        console.error('❌ [useDriverDetail] Failed to fetch online time:', err)
        return null
      }
    },
    [driverService, user?.id]
  )

  const fetchAllData = useCallback(async () => {
    if (!user?.id || !driverId) {
      return
    }

    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })

    try {
      await Promise.all([
        fetchDriverDetails(user.id, driverId),
        fetchDriverPerformance(driverId, state.dateRange),
      ])
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch driver data'
      dispatch({ type: 'SET_ERROR', payload: errorMessage })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [
    user?.id,
    driverId,
    state.dateRange,
    fetchDriverDetails,
    fetchDriverPerformance,
  ])

  const handlePeriodChange = useCallback((period: Period, customDates?: { startDate: string; endDate: string }) => {
    console.log('🔄 [useDriverDetail] Period changed:', period, customDates)
    dispatch({ type: 'SET_PERIOD', payload: period })

    const newRange = getPeriodDateRange(period, customDates)
    console.log('📅 [useDriverDetail] New date range:', newRange)
    dispatch({ type: 'SET_DATE_RANGE', payload: newRange })
  }, [])

  const handleDateRangeChange = useCallback(
    (range: DateRange) => {
      dispatch({ type: 'SET_DATE_RANGE', payload: range })
      if (state.selectedPeriod !== Period.Custom) {
        dispatch({ type: 'SET_PERIOD', payload: Period.Custom })
      }
    },
    [state.selectedPeriod]
  )

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id || !driverId) {
        return
      }

      dispatch({ type: 'SET_LOADING', payload: true })
      dispatch({ type: 'SET_ERROR', payload: null })

      try {
        await Promise.all([
          fetchDriverDetails(user.id, driverId),
          fetchDriverPerformance(driverId, state.dateRange),
        ])

        const previousDateRange = getPreviousPeriodDateRange(
          state.selectedPeriod
        )
        const previousRideMetrics = await fetchDriverRideMetrics(
          driverId,
          previousDateRange
        )

        const previousPerformanceResult =
          await driverService.getDriverPerformance({
            userId: user.id,
            driverId,
            period: state.selectedPeriod,
          })

        let previousRating = '0'
        if (
          isOk(previousPerformanceResult) &&
          previousPerformanceResult.value
        ) {
          previousRating =
            previousPerformanceResult.value.rating?.toFixed(1) || '0'
        }

        dispatch({
          type: 'SET_PREVIOUS_PERIOD_DATA',
          payload: {
            ridesCompleted: previousRideMetrics.totalRides,
            commission: previousRideMetrics.comissionEarnings,
            rating: previousRating,
          },
        })

        // Fetch online time
        const onlineTime = await fetchDriverOnlineTime(
          driverId,
          state.dateRange
        )
        if (onlineTime) {
          dispatch({ type: 'SET_ONLINE_TIME', payload: onlineTime })
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch driver data'
        dispatch({ type: 'SET_ERROR', payload: errorMessage })
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    }

    fetchData()
  }, [
    user?.id,
    driverId,
    state.dateRange,
    state.selectedPeriod,
    fetchDriverDetails,
    fetchDriverPerformance,
    fetchDriverRideMetrics,
    fetchDriverOnlineTime,
    driverService,
  ])

  const hasData = useMemo(() => !!state.driver, [state.driver])
  const hasAggregates = useMemo(() => !!state.aggregates, [state.aggregates])
  const hasError = useMemo(() => !!state.error, [state.error])

  const driverData = useMemo(() => {
    if (!state.driver) return null
    return {
      id: state.driver.id,
      name: state.driver.fullName,
      avatar: state.driver.photo || '',
      status: state.driver.isOnline
        ? ('online' as const)
        : ('offline' as const),
      rating: state.driver.rating.toFixed(1),
      classification: state.driver.classification || 'N/A',
      lastRideAt: state.driver.lastRideAt || 'N/A',
      phone: state.driver.phone || 'Não informado',
      email: state.driver.email || 'Não informado',
      address: state.driver.formattedAddress,
      joinDate: state.driver.createdAt,
    }
  }, [state.driver])

  const vehicleInfo = useMemo(() => {
    if (!state.driver?.car) {
      return {
        model: 'Veículo não cadastrado',
        plate: 'N/A',
        color: 'N/A',
      }
    }
    return {
      model: `${state.driver.car.brand} ${state.driver.car.model} ${state.driver.car.year}`,
      plate: state.driver.car.plate,
      color: state.driver.car.color,
    }
  }, [state.driver?.car])

  const documents = useMemo(() => {
    if (!state.driver) return null
    return {
      cnh: { status: 'valid', expires: state.driver.cnhExpiresAt || 'N/A' },
      ipva: { expires: state.driver.ipvaDisplay || 'N/A' },
      insurance: { expires: state.driver.insuranceStatus || 'N/A' },
    }
  }, [state.driver])

  const currentPeriodData = useMemo(() => {
    return state.aggregates
      ? {
          ridesCompleted: state.aggregates.totalRides,
          commission: state.aggregates.comissionEarnings,
          rating: state.aggregates.averageRating.toFixed(1),
        }
      : null
  }, [state.aggregates])

  const previousPeriodData = useMemo(() => {
    return (
      state.previousPeriodData || {
        ridesCompleted: 0,
        commission: 0,
        rating: '0',
      }
    )
  }, [state.previousPeriodData])

  const performanceData = useMemo(() => {
    const hoursWorked = state.onlineTime ? state.onlineTime.tempoTotal : 0
    const ridesCompleted = state.aggregates?.totalRides || 0
    const commission = state.aggregates?.comissionEarnings || 0

    return {
      hoursWorked,
      acceptanceRate: 0,
      commissionPerRide: ridesCompleted > 0 ? commission / ridesCompleted : 0,
      ridesPerHour: hoursWorked > 0 ? ridesCompleted / hoursWorked : 0,
    }
  }, [state.onlineTime, state.aggregates])

  return {
    driverData,
    vehicleInfo,
    documents,
    onlineTime: state.onlineTime,

    currentPeriodData,
    previousPeriodData,
    performanceData,

    selectedPeriod: state.selectedPeriod,
    dateRange: state.dateRange,
    isLoading: state.isLoading,
    hasError,
    error: state.error,

    handlePeriodChange,
    setDateRange: handleDateRangeChange,
    refreshData: fetchAllData,
    hasData,
    hasAggregates,
  }
}
